import { useMemo, useState } from "react";
import useAppDispatch from "@/hooks/useAppDispatch";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import { stripHTML } from "@/utils/utils";
import { handleApiError } from "@/helpers/apiErrorHelper";
import type { SelectedTerms } from "@/features/productVariations";
import type { ProductVariation } from "@/types/productVariations";
import { type Product, ProductType } from "@/types/products";
import type { AddToCartRequestPayload } from "@/types/api/wooStoreApi";
import type { Variation } from "@/types/cart";

//#region Type
interface AddToCartViewModelParams {
  product: Product | null;
  quantity: number;
  selectedTerms: SelectedTerms;
  matchedVariation: ProductVariation | null;
  getMatchedVariationId: () => number | null;
}
interface AddToCartCartViewModel {
  isAddToCartLoading: boolean;
  handleAddToCart: (onSuccess?: () => void) => Promise<void>;
  handleQuickAddToCartClick: (onRequireSelection: () => void) => void;
}
//#endregion

const useAddToCart = ({
  product,
  quantity,
  selectedTerms,
  matchedVariation,
  getMatchedVariationId,
}: AddToCartViewModelParams): AddToCartCartViewModel => {
  const appDispatch = useAppDispatch();

  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);

  const canAdd = useMemo(
    () =>
      !!product &&
      (product.type === ProductType.Simple ||
        (product.type === ProductType.Variable && !!matchedVariation)),
    [product, matchedVariation],
  );

  const buildVariationPayload = (variationId: number): Variation[] => {
    return Object.entries(selectedTerms).map(([selectedAttr]) => {
      const attributes = product?.variations?.find((v) => v.id === variationId)?.attributes;
      const selectedTerm = attributes?.find(
        (productAttr) => productAttr.name === selectedAttr,
      )?.value;

      return {
        attribute: selectedAttr,
        value: selectedTerm,
      };
    });
  };

  const buildAddToCartPayload = (): AddToCartRequestPayload => {
    if (product?.type === ProductType.Simple) {
      return {
        id: product.id,
        quantity,
        variation: {},
      };
    }

    if (product?.type === ProductType.Variable) {
      const variationId = getMatchedVariationId();

      if (!variationId) {
        throw new Error("未找到與選取的規格選項相對應的規格選項 id");
      }
      return {
        id: variationId,
        quantity,
        variation: buildVariationPayload(variationId),
      };
    }

    throw new Error(`不支援的商品類型(${product?.type})`);
  };

  // Quick 行為：只決定要不要引導補選規格
  const handleQuickAddToCartClick = (onRequireSelection: () => void) => {
    if (!product) return;

    if (product.type === ProductType.Simple) {
      void handleAddToCart();
      return;
    }

    if (product.type === ProductType.Variable) {
      if (matchedVariation) {
        void handleAddToCart();
      } else {
        onRequireSelection?.();
      }
    }
  };

  const handleAddToCart = async (onSuccess?: () => void) => {
    if (isAddToCartLoading) return;
    if (!canAdd) {
      appDispatch.pushAlert({
        type: "warning",
        title: "請選擇商品規格後再加入購物車",
      });
      return;
    }

    let isSuccess = false;
    setIsAddToCartLoading(true);

    try {
      const payload = buildAddToCartPayload();
      const { data: cartItem } = await wooStoreApiService.cartItem.addToCart(payload);
      appDispatch.addCartItem(cartItem);

      isSuccess = true;
      appDispatch.pushAlert({
        type: "success",
        title: `「${stripHTML(cartItem.name)}」已成功加入購物車`,
      });
    } catch (error) {
      const apiError = error as any;

      if (apiError?.data?.code === "woocommerce_rest_product_partially_out_of_stock") {
        appDispatch.pushAlert({
          type: "warning",
          title: "商品庫存不足，無法加入購物車。",
          desc: "可能原因：庫存不足或購物車中已有該商品且無法再次加入。",
        });
        return;
      }

      handleApiError(error, appDispatch, "加入購物車失敗，請查看錯誤或重新整理頁面再操作一次");
    } finally {
      setIsAddToCartLoading(false);
      if (isSuccess) {
        onSuccess?.();
      }
    }
  };

  return {
    isAddToCartLoading,
    handleAddToCart,
    handleQuickAddToCartClick,
  };
};

export default useAddToCart;
